import { z } from 'zod'
import {
  JobIdentifier,
  RecipeReference,
  RunnerCapabilitiesDto,
} from './monocleJob'
import { UTCInstant } from './localizedMessage'

// A policy ceiling, not a provisioned pool size. Each host has an approved limit.
export const WorkerConcurrency = z.number().int().min(1).max(64)
export const WorkerSecret = z.string().regex(/^[A-Za-z0-9_-]{32,256}$/)
export const WorkerName = z.string().trim().min(1).max(80)
export const WorkerPlatform = z.enum(['darwin', 'linux', 'win32'])
export const WorkerUserCode = z.string().regex(/^[A-Z2-9]{4}-[A-Z2-9]{4}$/)
export const EnrollmentBeginDto = z
  .object({
    installation_id: z.string().uuid(),
    name: WorkerName,
    platform: WorkerPlatform,
    proof: WorkerSecret,
  })
  .strict()
export const EnrollmentPollDto = z
  .object({ user_code: WorkerUserCode, proof: WorkerSecret })
  .strict()
export const EnrollmentApproveDto = z
  .object({
    user_code: WorkerUserCode,
    approve: z.boolean(),
    max_concurrency: WorkerConcurrency.default(16),
  })
  .strict()
export const EnrollmentChallengeDto = z.object({
  user_code: WorkerUserCode,
  expires_at: UTCInstant,
  interval: z.number().int().positive(),
})
export const EnrollmentDescriptionDto = z.object({
  user_code: WorkerUserCode,
  name: WorkerName,
  platform: WorkerPlatform,
  expires_at: UTCInstant,
  state: z.enum(['pending', 'approved', 'denied', 'redeemed']),
  reconnect: z.boolean(),
})
export const WorkerRuntimeStatus = z
  .object({
    id: JobIdentifier,
    state: z.enum([
      'starting',
      'idle',
      'running',
      'unavailable',
      'stopping',
      'paused',
      'disabled',
    ]),
    job_id: JobIdentifier.nullable(),
    percentage: z.number().min(0).max(100).nullable(),
    stage: z.string().max(1024).nullable(),
  })
  .strict()
export const WorkerHostFields = z.object({
  id: JobIdentifier,
  installation_id: z.string().uuid(),
  name: WorkerName,
  platform: WorkerPlatform,
  created_at: UTCInstant,
  approved_by: JobIdentifier,
  last_seen_at: UTCInstant.nullable(),
  disabled: z.boolean(),
  revoked: z.boolean(),
  accepting: z.boolean(),
  concurrency: WorkerConcurrency,
  max_concurrency: WorkerConcurrency,
  revision: z.number().int().nonnegative(),
  credential_epoch: z.number().int().positive(),
  pool: JobIdentifier,
  labels: z.array(JobIdentifier).max(16),
  approved_recipes: z.array(RecipeReference).max(32),
  capabilities: RunnerCapabilitiesDto.nullable(),
  runner_ids: z.array(JobIdentifier).max(64),
  disabled_runners: z.array(JobIdentifier).max(64),
  stop_requests: z.record(JobIdentifier, z.number().int().nonnegative()),
  observed: z.array(WorkerRuntimeStatus).max(64),
})
export function validateWorkerHost(
  value: z.infer<typeof WorkerHostFields>,
  context: z.RefinementCtx,
) {
  if (value.concurrency > value.max_concurrency)
    context.addIssue({
      code: 'custom',
      path: ['concurrency'],
      message: 'Concurrency exceeds host policy',
    })
  if (new Set(value.runner_ids).size !== value.runner_ids.length)
    context.addIssue({
      code: 'custom',
      path: ['runner_ids'],
      message: 'Duplicate runner identity',
    })
  for (const [field, ids] of [
    ['disabled_runners', value.disabled_runners],
    ['stop_requests', Object.keys(value.stop_requests)],
    ['observed', value.observed.map((row) => row.id)],
  ] as const) {
    if (
      ids.some((id) => !value.runner_ids.includes(id)) ||
      new Set(ids).size !== ids.length
    )
      context.addIssue({
        code: 'custom',
        path: [field],
        message: 'Runner identities must belong to this host',
      })
  }
  if ((value.disabled || value.revoked) && value.accepting)
    context.addIssue({
      code: 'custom',
      path: ['accepting'],
      message: 'Disabled or revoked hosts cannot accept work',
    })
  if (value.capabilities && value.capabilities.platform !== value.platform)
    context.addIssue({
      code: 'custom',
      path: ['capabilities'],
      message: 'Host platform mismatch',
    })
}
export const WorkerHostDto = WorkerHostFields.superRefine(validateWorkerHost)
export type WorkerHost = z.infer<typeof WorkerHostDto>
export const EnrollmentResultDto = z.discriminatedUnion('state', [
  z.object({ state: z.literal('pending') }),
  z.object({ state: z.literal('denied') }),
  z.object({
    state: z.literal('enrolled'),
    host: WorkerHostDto,
    token: WorkerSecret,
  }),
])
export const WorkerSyncDto = z
  .object({
    session_id: z.string().uuid(),
    capabilities: RunnerCapabilitiesDto,
    observed: z.array(WorkerRuntimeStatus).max(64).default([]),
  })
  .strict()
export const WorkerSyncResultDto = z.object({
  host: WorkerHostDto,
  server_time: UTCInstant,
  session_expires_at: UTCInstant,
  runners: z
    .array(
      z.object({
        id: JobIdentifier,
        token: WorkerSecret,
        enabled: z.boolean(),
      }),
    )
    .max(64),
})
export const WorkerControlDto = z.discriminatedUnion('action', [
  z.object({ action: z.literal('pause') }).strict(),
  ...(['resume', 'disable', 'enable', 'revoke'] as const).map((action) =>
    z.object({ action: z.literal(action) }).strict(),
  ),
  z
    .object({ action: z.literal('capacity'), value: WorkerConcurrency })
    .strict(),
  z.object({ action: z.literal('rename'), name: WorkerName }).strict(),
  ...(['disable_runner', 'enable_runner', 'stop_runner'] as const).map(
    (action) =>
      z
        .object({ action: z.literal(action), runner_id: JobIdentifier })
        .strict(),
  ),
] as const)

/** Private host lease fields used to fence claims in the job transaction. */
export const WorkerHostClaimPolicy = WorkerHostFields.pick({
  credential_epoch: true,
  disabled: true,
  revoked: true,
  accepting: true,
  concurrency: true,
  runner_ids: true,
  disabled_runners: true,
}).extend({
  session_id: z.string().uuid().nullable(),
  session_expires_at: UTCInstant.nullable(),
})
export function workerHostCanClaim(
  value: unknown,
  runner: { id: string; host_epoch?: number; host_session?: string },
  now: Date,
) {
  const result = WorkerHostClaimPolicy.safeParse(value)
  if (!result.success) return false
  const host = result.data
  return (
    !host.disabled &&
    !host.revoked &&
    host.accepting &&
    host.credential_epoch === runner.host_epoch &&
    host.session_id === runner.host_session &&
    !!host.session_expires_at &&
    Date.parse(host.session_expires_at) > now.getTime() &&
    host.runner_ids.indexOf(runner.id) >= 0 &&
    host.runner_ids.indexOf(runner.id) < host.concurrency &&
    !host.disabled_runners.includes(runner.id)
  )
}
